import React from "react";

import { css } from "@emotion/react";

import { ExtractSwarovskiPageData, PressReleasesPageData } from "@swo/storefront";
import { ContentContainer } from "../../components/_shared-components/arrangement/Container";
import { Spacer } from "../../components/_shared-components/arrangement/Space";
import { ContentfulImage } from "../../components/_shared-components/image-related/ContentfulImage";
import { SimpleAspectRatio } from "../../components/_shared-components/SimpleAspectRatio";
import { PageHeadlineMedium } from "../../components/_shared-components/ui-elements/PageHeadline";
import { SwarovskiLink } from "../../components/_shared-components/ui-elements/SwarovskiLink";
import { AdvertisementsCarouselComponent } from "../../components/AdvertisementsCarouselComponent/AdvertisementsCarouselComponent";
import { FooterComponent } from "../../components/FooterComponent/FooterComponent";
import { HeaderComponent } from "../../components/HeaderComponent/HeaderComponent";
import { IconsAndTextsComponent } from "../../components/IconsAndTextsComponent/IconsAndTextsComponent";
import { IconsAndTextsItem } from "../../components/IconsAndTextsComponent/IconsAndTextsItem";
import { PageMetadataComponent } from "../../components/PageMetadataComponent/PageMetadataComponent";
import { EXPERIMENTAL_TextWithProductNameRenderer } from "../../domain/swarovski-optik/products/product-name/product-name-renderer";
import { useStore } from "../../store/hooks/use-store";
import { CSS_MQ } from "../../styles/breakpoints";
import { fontColor, FONTS, fontSize } from "../../styles/fonts";
import { CSS_COLORS } from "../../styles/styles-css-variables";
import { plainTextMdStyles } from "../../styles/text-styles";
import { HeaderSpacer } from "../_layout-helpers/spacers";
import { PressReleaseDateLabel } from "./shared/PressReleaseDateLabel";
import { T } from "../../components/T";

export const PressReleasesPageLayout: React.FC<ExtractSwarovskiPageData<PressReleasesPageData>> = props => {
  const { locationOrigin } = useStore().state;

  const { locale } = props;

  const gridStyles = css`
    display: grid;
    grid-gap: 50px 30px;

    ${CSS_MQ.atLeastLg} {
      grid-gap: 70px 40px;
    }

    ${CSS_MQ.atLeastMd} {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  `;

  return (
    <React.Fragment>
      <PageMetadataComponent
        {...props.data.metadata}
        locationOrigin={locationOrigin}
        languageIsoCode={props.locale.language.isoCode}
      />
      <HeaderComponent header={props.data.header} version={"normal"} />
      <HeaderSpacer />

      <ContentContainer>
        <Spacer size={"md"} />
        <PageHeadlineMedium align={"center"}>
          <T>{props.data.pageHeadline}</T>
        </PageHeadlineMedium>
        <Spacer size={"md"} />
        {props.data.topIcons && (
          <React.Fragment>
            <IconsAndTextsComponent {...props.data.topIcons} />
            <Spacer size={"md"} />
          </React.Fragment>
        )}

        <div
          css={css`
            ${gridStyles};

            --aspect-ratio: 9/16;
          `}
        >
          {props.data.pressReleasesOverview.map(pressRelease => (
            <SwarovskiLink
              link={pressRelease.link}
              styled={"no-decoration"}
              isInternal={true}
              scrollToTop={true}
              key={pressRelease.headline.concat(pressRelease.date)}
              attachStyles={css`
                display: flex;
                flex-direction: column;

                img {
                  transition: transform 0.2s linear;
                }

                &:hover {
                  img {
                    transition: transform 0.2s linear;
                    transform: scale(1.025);
                  }
                }
              `}
            >
              <SimpleAspectRatio
                css={css`
                  overflow: hidden;
                  background-color: ${CSS_COLORS.Grey0};
                `}
              >
                <ContentfulImage
                  assetId={pressRelease.mainImage.sys.id}
                  contentfulLink={{
                    url: pressRelease.mainImage.url,
                    requestedWidth: 640,
                    noTransparentBackground: true,
                  }}
                  higherDPI={[2]}
                  alt={pressRelease.mainImage.description || pressRelease.mainImage.title}
                  css={css`
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                  `}
                />
              </SimpleAspectRatio>
              <span
                css={[
                  FONTS.SwarovskiFutura500,
                  fontColor.black,
                  fontSize.x2,
                  css`
                    margin-top: 14px;
                    margin-bottom: 6px;
                    ${CSS_MQ.atLeastMd} {
                      margin-top: 20px;
                      margin-bottom: 8px;
                    }

                    text-transform: uppercase;
                  `,
                ]}
              >
                <EXPERIMENTAL_TextWithProductNameRenderer text={pressRelease.subheadline} />
              </span>

              <span
                css={[
                  FONTS.MinervaModern500,
                  css`
                    ${plainTextMdStyles};

                    ${CSS_MQ.atLeastMd} {
                      font-size: 24px;
                      letter-spacing: 1.5px;
                      line-height: 28px;
                    }

                    ${CSS_MQ.atLeastLg} {
                      font-size: 30px;
                      line-height: 32px;
                    }

                    color: ${CSS_COLORS.Green};

                    text-transform: uppercase;
                  `,
                ]}
              >
                <EXPERIMENTAL_TextWithProductNameRenderer text={pressRelease.headline} />
              </span>

              <PressReleaseDateLabel dateStr={pressRelease.date} locale={locale} />
            </SwarovskiLink>
          ))}
        </div>

        {props.data.contactData && (
          <React.Fragment>
            <div
              css={css`
                text-align: center;
              `}
            >
              <Spacer size="md" />
              <IconsAndTextsItem
                headline={<T>{props.data.contactData.headline}</T>}
                icon={{
                  title: props.data.contactData.icon.description || props.data.contactData.icon.title || undefined,
                  url: props.data.contactData.icon.url,
                }}
                text={props.data.contactData.text}
                ctaButton={props.data.contactData.ctaButton}
              />
            </div>
          </React.Fragment>
        )}

        <Spacer size={"md"} />
      </ContentContainer>
      {props.data.advertisementsCarousel && (
        <React.Fragment>
          <AdvertisementsCarouselComponent {...props.data.advertisementsCarousel} />
          <Spacer size="sm" />
        </React.Fragment>
      )}

      <FooterComponent {...props.data.footer} />
    </React.Fragment>
  );
};
